import { useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Plus,
} from "lucide-react";
import { addYearTab, getYearTabList } from "../../apis/Business/Business";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useSim } from "../../app/Company/SimContext";
import "./index.css";

const VISIBLE_YEARS = 6;

const handleAddYear = (yearFormData, setYearTabList, setYearData) => {
  if (/^\d+$/.test(yearFormData)) {
    addYearTab({
      yearData: yearFormData,
      callback: () => {
        getYearTabList({ setYearTabList: setYearTabList });
        setYearData(yearFormData);
      },
    });
  } else {
    Swal.fire({
      title: "Error!",
      icon: "error",
      text: "Please input a valid year",
      confirmButtonColor: "#a9c1b4",
      color: "#476355",
      background: "#f1f8ef",
    });
  }
};

/** Strip trailing slashes so `/product/` matches `/product`. */
export function normalizeAppPathname(pathname) {
  const s = String(pathname || "").trim();
  const noTrail = s.replace(/\/+$/, "");
  return noTrail || "/";
}

/**
 * Routes that show the year strip + sync `?year=` on the URL.
 * Product catalog (`/product`, `/product/list`) intentionally has no year strip.
 */
export function shouldShowYearTabForPath(pathname) {
  const path = normalizeAppPathname(pathname);
  if (path === "/product" || path === "/product/list") return false;
  if (path.startsWith("/product/list/")) return false;
  if (
    /^\/product\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      path
    )
  ) {
    return false;
  }

  const showedLocation = ["/company", "/ambition", "/reporting", "/client"];
  return showedLocation.some(
    (p) => path === p || path.startsWith(p + "/")
  );
}

/**
 * Where Skeleton should mirror `?year=` into YearTabContext when the URL changes.
 * Excludes product catalog (same as year strip visibility).
 */
export function shouldSyncYearFromUrlForPath(pathname) {
  const path = normalizeAppPathname(pathname);
  if (path === "/product" || path === "/product/list") return false;
  if (path.startsWith("/product/list/")) return false;

  const showedLocation = ["/company", "/ambition", "/reporting", "/client"];
  return showedLocation.some(
    (p) => path === p || path.startsWith(p + "/")
  );
}

const YearTab = ({ setYearParent }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { simMode, setSimMode } = useSim();
  const [collapsed, setCollapsed] = useState(false);
  const [showYearForm, setShowYearForm] = useState(false);
  const [yearData, setYearData] = useState("");
  const [yearFormData, setYearFormData] = useState("");
  const [yearTabList, setYearTabList] = useState([]);
  const [baselineYear, setBaselineYear] = useState();
  const [yearOffset, setYearOffset] = useState(0);

  const sortedYears = useMemo(
    () => [...yearTabList].sort((a, b) => Number(b) - Number(a)),
    [yearTabList]
  );

  const maxYearOffset = Math.max(0, sortedYears.length - VISIBLE_YEARS);

  const visibleYears = useMemo(
    () => sortedYears.slice(yearOffset, yearOffset + VISIBLE_YEARS),
    [sortedYears, yearOffset]
  );

  useEffect(() => {
    setYearOffset((o) => Math.min(o, maxYearOffset));
  }, [maxYearOffset]);

  useEffect(() => {
    if (!yearData || sortedYears.length === 0) return;
    const idx = sortedYears.indexOf(yearData);
    if (idx < 0) return;
    setYearOffset((o) => {
      if (idx < o) return Math.max(0, idx);
      if (idx >= o + VISIBLE_YEARS) {
        return Math.min(maxYearOffset, idx - VISIBLE_YEARS + 1);
      }
      return Math.min(o, maxYearOffset);
    });
  }, [yearData, sortedYears, maxYearOffset]);

  useEffect(() => {
    if (!shouldShowYearTabForPath(location.pathname)) return;
    const queryParams = new URLSearchParams(location.search);
    if (yearData) {
      queryParams.set("year", yearData);
    }
    const newUrl = `${location.pathname}?${queryParams.toString()}`;
    window.history.replaceState(null, null, newUrl);
  }, [yearData, location.pathname, location.search]);

  setYearParent(yearData);

  useEffect(() => {
    getYearTabList({
      setYearTabList: setYearTabList,
      setBaselineYear: setBaselineYear,
      callback: (year) => setYearData(year),
    });
  }, []);

  const shouldShowYearTab = shouldShowYearTabForPath(location.pathname);
  const simStrip =
    shouldShowYearTab &&
    normalizeAppPathname(location.pathname).startsWith("/company");
  const isReporting =
    normalizeAppPathname(location.pathname) === "/reporting";

  return (
    <>
      {shouldShowYearTab && (
        <div className="yearTab yearPickerStrip">
          <button
            type="button"
            className="yearPickerStrip__collapse"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label="Toggle year picker"
          >
            <ChevronLeft
              className={
                "yearPickerStrip__collapseIcon" +
                (collapsed ? " yearPickerStrip__collapseIcon--rotated" : "")
              }
            />
          </button>

          {!collapsed && (
            <>
              {simStrip ? (
                <div className="yearPickerSim__wrap">
                  <button
                    type="button"
                    className={
                      "yearPickerSim__btn" +
                      (simMode ? " yearPickerSim__btn--active" : "")
                    }
                    onClick={() => setSimMode((m) => !m)}
                    aria-pressed={simMode}
                    aria-label={t("SIMULATE")}
                  >
                    <span className="yearPickerSim__title">{t("SIMULATE")}</span>
                    <div
                      className={
                        "yearPickerSim__hintGrid" +
                        (simMode ? " yearPickerSim__hintGrid--open" : "")
                      }
                    >
                      <span className="yearPickerSim__hintInner">
                        Klik på en virksomhed for at oprette et simuleret flow
                      </span>
                    </div>
                  </button>
                  <span className="yearPickerSim__badge" aria-hidden>
                    <Plus className="yearPickerSim__plusIcon" />
                  </span>
                </div>
              ) : (
                <div className="yearPickerStrip__simulateLegacy text-white p-8">
                  <h5
                    className="text-center"
                    style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}
                  >
                    {t("SIMULATE")}
                  </h5>
                </div>
              )}

              {!isReporting && (
                <button
                  type="button"
                  className="yearPickerStrip__iconBtn"
                  aria-label="Add year tab"
                  onClick={() => setShowYearForm((v) => !v)}
                >
                  <Plus className="yearPickerStrip__iconBtnInner" />
                </button>
              )}

              {sortedYears.length > VISIBLE_YEARS && (
                <button
                  type="button"
                  className="yearPickerStrip__scroll"
                  aria-label="Scroll years up"
                  disabled={yearOffset === 0}
                  onClick={() =>
                    setYearOffset((o) => Math.max(0, o - 1))
                  }
                >
                  <ChevronUp className="yearPickerStrip__scrollIcon" />
                </button>
              )}

              {visibleYears.map((y) => {
                const selected = yearData === y;
                const isBaseline = baselineYear && y === baselineYear;
                return (
                  <button
                    key={y}
                    type="button"
                    className={
                      "yearPickerStrip__year" +
                      (selected ? " yearPickerStrip__year--selected" : "") +
                      (isBaseline ? " yearPickerStrip__year--baseline" : "")
                    }
                    onClick={() => setYearData(y)}
                  >
                    {y}
                  </button>
                );
              })}

              {sortedYears.length > VISIBLE_YEARS && (
                <button
                  type="button"
                  className="yearPickerStrip__scroll"
                  aria-label="Scroll years down"
                  disabled={yearOffset >= maxYearOffset}
                  onClick={() =>
                    setYearOffset((o) => Math.min(maxYearOffset, o + 1))
                  }
                >
                  <ChevronDown className="yearPickerStrip__scrollIcon" />
                </button>
              )}
            </>
          )}

          {showYearForm && (
            <div className="yearPickerStrip__form">
              <h5 className="yearPickerStrip__formTitle">Add Year Tab</h5>
              <input
                type="text"
                className="yearPickerStrip__formInput"
                placeholder="Year"
                value={yearFormData}
                onChange={(e) => setYearFormData(e.target.value)}
                maxLength={4}
              />
              <button
                type="button"
                className="yearPickerStrip__formSubmit"
                onClick={() => {
                  const ok = /^\d+$/.test(yearFormData);
                  handleAddYear(yearFormData, setYearTabList, setYearData);
                  if (ok) {
                    setShowYearForm(false);
                    setYearFormData("");
                  }
                }}
              >
                Submit
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default YearTab;
